import { NavLink } from 'react-router-dom';
import {
  Clock,
  FileText,
  HeartHandshake,
  ClipboardCheck,
  Tags,
  LogOut,
  type LucideIcon,
} from 'lucide-react';
import type { EmployeePublic } from '@renewal/types';

/**
 * AppSidebar — the ink suite left-nav for renewal-timesheets. Replaces the old
 * green top-bar nav (`.app-nav`) that lived in App.tsx. Styling comes entirely
 * from the `.ri-sidebar*` classes in renewal-theme/theme.css (identical to the
 * rest of the suite); this is driven by THIS app's REAL routes — restyled,
 * never reorganized.
 *
 * Preserved from the old nav (do NOT change — e2e + parity depend on them):
 *  - labels / order / paths: Timesheets(/timesheet), Reports(/reports), and the
 *    supervisor-only Volunteer Center(/dashboard), Supervisor Review(/review),
 *    Task Codes(/task-codes).
 *  - data-testids: nav-timesheets, nav-reports, nav-dashboard, review-queue-badge,
 *    nav-user-name, and auth-logout-button (Sign Out, pinned bottom-left).
 */
interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  testId?: string;
  supervisorOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/timesheet', label: 'Timesheets', icon: Clock, testId: 'nav-timesheets' },
  { to: '/reports', label: 'Reports', icon: FileText, testId: 'nav-reports' },
  {
    to: '/dashboard',
    label: 'Volunteer Center',
    icon: HeartHandshake,
    testId: 'nav-dashboard',
    supervisorOnly: true,
  },
  { to: '/review', label: 'Supervisor Review', icon: ClipboardCheck, supervisorOnly: true },
  { to: '/task-codes', label: 'Task Codes', icon: Tags, supervisorOnly: true },
];

interface AppSidebarProps {
  user: EmployeePublic | null;
  isSupervisor: boolean;
  /** Pending supervisor-review count — renders a gold badge on Supervisor Review. */
  pendingReviewCount: number;
  /** Sign out (react-oidc-context signoutRedirect, via useAuth.logout). */
  onSignOut: () => void;
}

export function AppSidebar({ user, isSupervisor, pendingReviewCount, onSignOut }: AppSidebarProps) {
  const visibleItems = NAV_ITEMS.filter((item) => !item.supervisorOnly || isSupervisor);

  return (
    <aside className="ri-sidebar" aria-label="Timesheets navigation">
      <div className="ri-sidebar-eyebrow">Timesheets</div>

      <nav className="ri-sidebar-nav ri-sidebar-scroll" aria-label="Main navigation">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const showBadge = item.to === '/review' && pendingReviewCount > 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              aria-label={
                showBadge ? `${item.label} (${pendingReviewCount} pending)` : undefined
              }
              className={({ isActive }) =>
                `ri-sidebar-item${isActive ? ' ri-sidebar-item--active' : ''}`
              }
            >
              <Icon size={18} strokeWidth={1.8} className="ri-sidebar-icon" aria-hidden />
              <span>{item.label}</span>
              {showBadge && (
                <span className="ri-sidebar-badge" data-testid="review-queue-badge" aria-hidden="true">
                  {pendingReviewCount}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="ri-sidebar-footer ri-sidebar-footer--ruled">
        {user && (user.name || user.email) && (
          <div className="ri-sidebar-identity">
            {user.name && (
              <div className="ri-sidebar-identity-name" data-testid="nav-user-name">
                {user.name}
              </div>
            )}
            {user.email && <div className="ri-sidebar-identity-email">{user.email}</div>}
          </div>
        )}
        <button
          type="button"
          className="ri-sidebar-item"
          data-testid="auth-logout-button"
          onClick={onSignOut}
          aria-label="Sign out of your account"
        >
          <LogOut size={18} strokeWidth={1.8} className="ri-sidebar-icon" aria-hidden />
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
